import { motion } from 'framer-motion'
import { Globe, BarChart3, Smartphone, Search } from 'lucide-react'

const services = [
  {
    icon: Globe,
    title: 'Weboldal készítés',
    description: 'Modern, gyors és ügyfélszerző weboldalak, amelyek mobilon is tökéletesen működnek.',
    features: ['Reszponzív dizájn', 'SEO optimalizált', 'Gyors betöltés'],
  },
  {
    icon: BarChart3,
    title: 'Google Ads',
    description: 'Azonnali forgalom és mérhető eredmények. Célzott hirdetések, amelyek konvertálnak.',
    features: ['Kulcsszó kutatás', 'Kampány optimalizálás', 'ROI követés'],
  },
  {
    icon: Smartphone,
    title: 'Social Media kezelés',
    description: 'Facebook és Instagram jelenlét építése. Tartalom, ami eladja a terméked.',
    features: ['Tartalomgyártás', 'Közösségépítés', 'Hirdetés kezelés'],
  },
  {
    icon: Search,
    title: 'SEO',
    description: 'Hosszú távú növekedés a Google-ben. Organikus látogatók, akik keresnek téged.',
    features: ['Technikai SEO', 'Tartalomstratégia', 'Link építés'],
  },
]

const ServiceCard = ({ service, index }) => {
  const Icon = service.icon
  
  return (
    <motion.div
      className="group relative bg-white rounded-2xl p-8 service-card border border-coffee-100/50 hover:border-coffee-300/50"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      {/* Icon */}
      <div className="w-14 h-14 bg-coffee-100 rounded-xl flex items-center justify-center mb-6 group-hover:bg-coffee-600 transition-colors duration-300">
        <Icon 
          size={28} 
          className="text-coffee-600 group-hover:text-white transition-colors duration-300" 
        />
      </div>

      {/* Content */}
      <h3 className="font-heading text-xl font-bold text-coffee-900 mb-3">
        {service.title}
      </h3>
      <p className="text-coffee-600 leading-relaxed mb-6">
        {service.description}
      </p>

      {/* Features */}
      <ul className="space-y-2">
        {service.features.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-sm text-coffee-700">
            <span className="w-1.5 h-1.5 bg-coffee-400 rounded-full" />
            {feature}
          </li>
        ))}
      </ul>

      {/* Hover decoration */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-coffee-100/0 group-hover:bg-coffee-100/50 rounded-bl-[80px] transition-all duration-300" />
    </motion.div>
  )
}

const Services = () => {
  return (
    <section id="szolgaltatasok" className="py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="max-w-3xl mb-16 md:mb-20">
          <motion.span
            className="text-coffee-600 text-sm font-medium tracking-widest uppercase block mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Szolgáltatások
          </motion.span>
          <motion.h2
            className="font-heading text-4xl md:text-5xl font-bold text-coffee-900 mb-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Mindent egy kézből
          </motion.h2>
          <motion.p
            className="text-lg text-coffee-600 leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
          >
            Nem elég jelen lenni – látszani is kell. Átfogó marketing megoldások, 
            amelyek együtt dolgoznak a sikeredért.
          </motion.p>
        </div>

        {/* Free consultation banner */}
        <motion.div
          className="mb-12 p-6 md:p-8 bg-coffee-900 rounded-2xl text-cream"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-heading text-2xl font-bold mb-2">
                Ingyenes 30 perces konzultáció
              </h3>
              <p className="text-coffee-200">
                Beszéljük át, hogyan segíthetek a vállalkozásodnak!
              </p>
            </div>
            <button
              onClick={() => {
                const el = document.querySelector('#kapcsolat')
                if (el) el.scrollIntoView({ behavior: 'smooth' })
              }}
              className="flex-shrink-0 bg-cream text-coffee-900 px-8 py-3 rounded-full font-semibold hover:bg-coffee-100 transition-colors duration-200"
              data-testid="services-cta-button"
            >
              Foglalj időpontot
            </button>
          </div>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard key={service.title} service={service} index={index} />
          ))}
        </div>
      </div>
    </section>
  )
}

export default Services
